import * as React from "react";
import s from "./curiosidades.scss";
import { MultiPlaceRetrospectiveData, RetrospectiveData } from "../../api/types";
import { AnimatedCuriosidadeCard } from "./animated-curiosidade-card";
import { curiosidadesContent } from "./content";
import { AnimatedNumber } from "../../utils/use-count-up";
import { Category, getLabelByCategory } from "../resumo/content";
import { formatCompactNumber, formatNumber } from "../../utils/format-number";
import { replacePlaceholders } from "../../utils/replace-placeholders";
import { PlaceCarousel } from "../place-carousel";

interface CuriosidadeCardProps {
	curiosity: {
		id: string;
		image: string;
		title: string;
		number: string | number;
		color: string;
		description: string;
	};
	delay?: number;
}

function CuriosidadeCard({ curiosity, delay }: CuriosidadeCardProps) {
	const decimals = typeof curiosity.number === "number" && curiosity.number < 10 ? 1 : 0;
	const formattedNumber =
		typeof curiosity.number === "number"
			? formatNumber(curiosity.number, decimals)
			: curiosity.number;

	const formattedDescription = replacePlaceholders(curiosity.description, {
		value: formattedNumber,
	});

	return (
		<AnimatedCuriosidadeCard className={s.curiosidadeCard} delay={delay}>
			<img src={curiosity.image} alt={curiosity.title} />
			<div className={s.curiosidadeOverlay} />
			<div className={s.curiosidadeContent}>
				<h3 className={s[`text${curiosity.color}`]}>{formattedNumber}x</h3>
				<p className={s.curiosidadeTitle}>{curiosity.title}</p>
				<p className={s.curiosidadeDescription}>{formattedDescription}</p>
			</div>
		</AnimatedCuriosidadeCard>
	);
}

interface CuriosidadesProps {
	curiosities: RetrospectiveData["curiosities"];
	summary: RetrospectiveData["summary"];
	type: RetrospectiveData["type"];
	operatingTime: RetrospectiveData["operatingTime"];
	qtd_litros_cervejas: number;
	multiPlaceData?: MultiPlaceRetrospectiveData;
	selectedPlaceIndex?: number;
	onPlaceIndexChange?: (index: number) => void;
	mixedData?: RetrospectiveData["mixedData"];
}

export function Curiosidades({
	curiosities,
	summary,
	type,
	operatingTime,
	qtd_litros_cervejas,
	multiPlaceData,
	selectedPlaceIndex = 0,
	onPlaceIndexChange,
	mixedData,
}: CuriosidadesProps) {
	const currentData = React.useMemo(() => {
		if (!multiPlaceData)
			return {
				curiosities,
				summary,
				type,
				mixedData,
				operatingTime,
				qtd_litros_cervejas,
			};

		const places = multiPlaceData.places;

		// Verificação de segurança: índice válido
		if (selectedPlaceIndex < 0 || selectedPlaceIndex >= places.length) {
			console.warn(
				`Curiosidades: selectedPlaceIndex ${selectedPlaceIndex} está fora do range (0-${
					places.length - 1
				})`,
			);
			return {
				curiosities,
				summary,
				type,
				mixedData,
				operatingTime,
				qtd_litros_cervejas,
			};
		}

		const currentPlace = places[selectedPlaceIndex];

		// Verificação de segurança: place existe
		if (!currentPlace) {
			console.warn(
				`Curiosidades: currentPlace não encontrado no índice ${selectedPlaceIndex}`,
			);
			return {
				curiosities,
				summary,
				type,
				mixedData,
				operatingTime,
				qtd_litros_cervejas,
			};
		}

		console.log(
			"Curiosidades - currentPlace:",
			currentPlace.id,
			"selectedPlaceIndex:",
			selectedPlaceIndex,
		);

		if (currentPlace.id === "aggregated") {
			return {
				curiosities: multiPlaceData.aggregated.curiosities,
				summary: multiPlaceData.aggregated.summary,
				type: multiPlaceData.aggregated.type,
				mixedData: multiPlaceData.aggregated.mixedData,
				operatingTime: multiPlaceData.aggregated.operatingTime,
				qtd_litros_cervejas: multiPlaceData.aggregated.qtd_litros_cervejas,
			};
		}

		const placeData = multiPlaceData.byPlace[currentPlace.id];
		if (!placeData) {
			console.warn(
				`Curiosidades: dados não encontrados para place ${currentPlace.id}`,
				multiPlaceData.byPlace,
			);
			return {
				curiosities,
				summary,
				type,
				mixedData,
				operatingTime,
				qtd_litros_cervejas,
			};
		}

		return {
			curiosities: placeData.curiosities,
			summary: placeData.summary,
			type: placeData.type,
			mixedData: placeData.mixedData,
			operatingTime: placeData.operatingTime,
			qtd_litros_cervejas: placeData.qtd_litros_cervejas,
		};
	}, [
		multiPlaceData,
		selectedPlaceIndex,
		curiosities,
		summary,
		type,
		mixedData,
		operatingTime,
		qtd_litros_cervejas,
	]);

	const isMixed = currentData.type === "mixed";
	const currentMixedData = currentData.mixedData || mixedData;

	// Função para agrupar curiosidades por categoria
	const groupCuriositiesByCategory = (
		curiositiesList: RetrospectiveData["curiosities"],
	): Record<Category, RetrospectiveData["curiosities"]> => {
		const grouped: Record<Category, RetrospectiveData["curiosities"]> = {
			people: [],
			products: [],
			beer: [],
			operationTime: [],
		};

		curiositiesList.forEach(curiosity => {
			if (curiosity.category) {
				grouped[curiosity.category].push(curiosity);
			}
		});

		return grouped;
	};

	// Função para obter o big number de cada categoria
	const getBigNumberByCategory = (category: Category): number => {
		switch (category) {
			case "people":
				return currentData.summary.total;
			case "products":
				return currentData.summary.total;
			case "beer":
				return currentData.qtd_litros_cervejas;
			case "operationTime":
				return currentData.operatingTime.minutesInYear;
		}
	};

	// Renderizar seção de categoria
	const renderCategorySection = (
		category: Category,
		curiositiesList: RetrospectiveData["curiosities"],
	) => {
		if (curiositiesList.length === 0) return null;

		return (
			<>
				<div className={s.curiosidadesHeader}>
					<h3 className={s.bigNumber}>
						<AnimatedNumber
							value={getBigNumberByCategory(category)}
							duration={1200}
							prefix="+"
							formatNumber={formatCompactNumber}
						/>
					</h3>
					<p className={s.bigNumberLabel}>{getLabelByCategory(category)}</p>
				</div>
				<div className={s.curiosidadesList}>
					{curiositiesList.map((curiosity, index) => (
						<CuriosidadeCard
							key={curiosity.id}
							curiosity={curiosity}
							delay={index * 100}
						/>
					))}
				</div>
			</>
		);
	};

	// Ordem de exibição das categorias
	const categoryOrder: Category[] = ["people", "products", "beer", "operationTime"];

	return (
		<section id="curiosidades" className={s.curiosidades}>
			<div className={s.sectionContainer}>
				<h2 className={s.sectionTitle}>{curiosidadesContent.title}</h2>

				{multiPlaceData && onPlaceIndexChange && (
					<PlaceCarousel
						places={multiPlaceData.places}
						currentIndex={selectedPlaceIndex}
						onChangeIndex={onPlaceIndexChange}
					/>
				)}

				{isMixed && currentMixedData ? (
					<>
						{(() => {
							// Combinar curiosidades de cashless e ficha, removendo duplicatas por id
							const allCuriosities = [
								...currentMixedData.cashless.curiosities,
								...currentMixedData.ficha.curiosities,
							];

							// Remover duplicatas mantendo apenas a primeira ocorrência
							const uniqueCuriosities = allCuriosities.filter(
								(curiosity, index, self) =>
									index === self.findIndex(c => c.id === curiosity.id),
							);

							// Agrupar por categoria
							const grouped = groupCuriositiesByCategory(uniqueCuriosities);

							// Função para obter o big number por categoria no modo mixed
							const getBigNumberForMixed = (category: Category): number => {
								if (category === "people") return currentMixedData.cashless.summary.total;
								if (category === "products") return currentMixedData.ficha.summary.total;
								if (category === "beer") return currentData.qtd_litros_cervejas;
								if (category === "operationTime")
									return currentData.operatingTime.minutesInYear;
								return 0;
							};

							return categoryOrder.map(category => {
								const curiositiesList = grouped[category];
								if (curiositiesList.length === 0) return null;

								return (
									<React.Fragment key={`mixed-${category}`}>
										<div className={s.curiosidadesHeader}>
											<h3 className={s.bigNumber}>
												<AnimatedNumber
													value={getBigNumberForMixed(category)}
													duration={1200}
													prefix="+"
													formatNumber={formatCompactNumber}
												/>
											</h3>
											<p className={s.bigNumberLabel}>{getLabelByCategory(category)}</p>
										</div>
										<div className={s.curiosidadesList}>
											{curiositiesList.map((curiosity, index) => (
												<CuriosidadeCard
													key={curiosity.id}
													curiosity={curiosity}
													delay={index * 100}
												/>
											))}
										</div>
									</React.Fragment>
								);
							});
						})()}
					</>
				) : (
					<>
						{(() => {
							const grouped = groupCuriositiesByCategory(currentData.curiosities);
							return categoryOrder.map(category => {
								const curiositiesList = grouped[category];
								return renderCategorySection(category, curiositiesList);
							});
						})()}
					</>
				)}
			</div>
		</section>
	);
}
